import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient"; // centralized Axios instance

const ENDPOINT = "/training-schedules/";

// ─── Async Thunks ───

// Fetch all training schedules
export const fetchTrainingSchedules = createAsyncThunk(
  "trainingSchedules/fetchTrainingSchedules",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(ENDPOINT);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch training schedules");
    }
  }
);

// Insert training schedule
export const insertTrainingSchedule = createAsyncThunk(
  "trainingSchedules/insertTrainingSchedule",
  async (schedulePayload, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(ENDPOINT, schedulePayload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to create training schedule");
    }
  }
);

// Update schedule
export const updateTrainingSchedule = createAsyncThunk(
  "trainingSchedules/updateTrainingSchedule",
  async (schedule, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`${ENDPOINT}${schedule.id}/`, schedule);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update training schedule");
    }
  }
);

// Delete schedule
export const deleteTrainingSchedule = createAsyncThunk(
  "trainingSchedules/deleteTrainingSchedule",
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`${ENDPOINT}${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete training schedule");
    }
  }
);

// ─── Slice ───

const trainingSchedulesSlice = createSlice({
  name: "trainingSchedules",
  initialState: {
    schedules: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchTrainingSchedules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrainingSchedules.fulfilled, (state, action) => {
        state.loading = false;
        state.schedules = action.payload;
      })
      .addCase(fetchTrainingSchedules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Insert
      .addCase(insertTrainingSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(insertTrainingSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.schedules.push(action.payload);
      })
      .addCase(insertTrainingSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update
      .addCase(updateTrainingSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTrainingSchedule.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.schedules.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.schedules[index] = action.payload;
        }
      })
      .addCase(updateTrainingSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteTrainingSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTrainingSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.schedules = state.schedules.filter(s => s.id !== action.payload);
      })
      .addCase(deleteTrainingSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default trainingSchedulesSlice.reducer;
